/**
 * @module ArtefactListModal
 * @purpose Read-only modal displaying all artefacts in a template, grouped by name prefix
 * @depends react-i18next, lucide-react, ArtefactConfig type
 * @dependedOnBy ArtefactTagList
 *
 * @rules
 * - State: no internal state; controlled via isOpen/onClose props
 * - Performance: renders full artefact list in a scrollable container
 * - Errors: no API calls, display-only
 */

import { useEffect, type ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Package } from 'lucide-react'
import type { ArtefactConfig } from '../../types/velociraptor'

const NAME_SEGMENT_COUNT = 2
const ESCAPE_KEY = 'Escape'
const MODAL_TITLE_ID = 'artefact-list-modal-title'

interface ArtefactListModalProps {
  artefacts: ArtefactConfig[]
  templateName: string
  isOpen: boolean
  onClose: () => void
}

function getGroupKey(name: string): string {
  const segments = name.split('.')
  if (segments.length < 2) return ''
  return segments.slice(0, NAME_SEGMENT_COUNT).join('.')
}

function groupArtefacts(artefacts: ArtefactConfig[]): Map<string, ArtefactConfig[]> {
  const groups = new Map<string, ArtefactConfig[]>()
  for (const artefact of artefacts) {
    const key = getGroupKey(artefact.name)
    const existing = groups.get(key)
    if (existing) {
      existing.push(artefact)
    } else {
      groups.set(key, [artefact])
    }
  }
  return groups
}

export function ArtefactListModal({
  artefacts,
  templateName,
  isOpen,
  onClose,
}: ArtefactListModalProps): ReactElement | null {
  const { t } = useTranslation('cases')

  useEffect(() => {
    if (!isOpen) return
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === ESCAPE_KEY) {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const groups = groupArtefacts(artefacts)
  const ungroupedLabel = t('velociraptor.artefactList.ungroupedCategory', 'Other')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby={MODAL_TITLE_ID}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-lg flex-col rounded-xl bg-white shadow-xl dark:bg-slate-900"
        onClick={(e) => {
          e.stopPropagation()
        }}
      >
        <ModalHeader templateName={templateName} onClose={onClose} />
        <ModalContent groups={groups} ungroupedLabel={ungroupedLabel} />
        <ModalFooter artefactCount={artefacts.length} onClose={onClose} />
      </div>
    </div>
  )
}

interface ModalHeaderProps {
  templateName: string
  onClose: () => void
}

function ModalHeader({ templateName, onClose }: ModalHeaderProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
      <div className="flex items-center gap-2">
        <Package className="h-5 w-5 text-slate-500 dark:text-slate-400" />
        <h3
          id={MODAL_TITLE_ID}
          className="font-heading text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          {t('velociraptor.artefactList.modalTitle', 'Artefacts in {{templateName}}', {
            templateName,
          })}
        </h3>
      </div>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
        aria-label={t('velociraptor.artefactList.close', 'Close')}
      >
        <X className="h-5 w-5" />
      </button>
    </div>
  )
}

interface ModalContentProps {
  groups: Map<string, ArtefactConfig[]>
  ungroupedLabel: string
}

function ModalContent({ groups, ungroupedLabel }: ModalContentProps): ReactElement {
  return (
    <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
      {[...groups.entries()].map(([groupKey, groupArtefacts]) => (
        <ArtefactGroup
          key={groupKey}
          groupKey={groupKey}
          groupArtefacts={groupArtefacts}
          ungroupedLabel={ungroupedLabel}
        />
      ))}
    </div>
  )
}

interface ArtefactGroupProps {
  groupKey: string
  groupArtefacts: ArtefactConfig[]
  ungroupedLabel: string
}

function ArtefactGroup({
  groupKey,
  groupArtefacts,
  ungroupedLabel,
}: ArtefactGroupProps): ReactElement {
  const label = groupKey === '' ? ungroupedLabel : groupKey
  return (
    <div>
      <h4 className="mb-2 text-xs font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
        {label}
      </h4>
      <ul className="space-y-1">
        {groupArtefacts.map((artefact) => (
          <li key={artefact.name} className="py-0.5 text-sm text-slate-700 dark:text-slate-300">
            {artefact.name}
          </li>
        ))}
      </ul>
    </div>
  )
}

interface ModalFooterProps {
  artefactCount: number
  onClose: () => void
}

function ModalFooter({ artefactCount, onClose }: ModalFooterProps): ReactElement {
  const { t } = useTranslation('cases')
  return (
    <div className="flex flex-shrink-0 items-center justify-between border-t border-slate-200 px-6 py-4 dark:border-slate-700">
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {t('collections.artefactTemplates.artefactCount', { count: artefactCount })}
      </span>
      <button
        type="button"
        onClick={onClose}
        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        {t('velociraptor.artefactList.close', 'Close')}
      </button>
    </div>
  )
}
